import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { type ListViewBitFlagsMasks, getIsBitSet } from "owa-bit-flags/lib/utils/listViewBitFlagsUtil";
import { setMoveCopyAttachCrossAccountDialogStateMutator } from "../mutators/moveCopyAttachCrossAccountDialogMutators";
import { crossAccountNotAllowedMoveTitleText, crossAccountNotAllowedCopyTitleText, crossAccountNotAllowedAttachTitleText, crossAccountNotAllowedCategorizeTitleText, copyCrossAccountNotAllowedContentText, moveCrossAccountNotAllowedContentText, attachCrossAccountNotAllowedContentText, categorizeCrossAccountNotAllowedContentText, movingEmailConfirmDialogTitle, copyingEmailConfirmDialogTitle, attachingFileConfirmDialogTitle, categorizingEmailConfirmDialogTitle, movingEmailConfirmMessage, copyingEmailConfirmMessage, attachingFileConfirmMessage, categorizingEmailConfirmMessage, } from "../locstrings/moveCopyDialog.locstring.json";
import loc, { format } from "owa-localize";
import { assertNever } from "owa-assert";
import { getDestinationMailboxInfo, isMoveCopyCrossAccount, isMoveCopyCrossAccountNotAllowedPolicy } from "./copyMoveItemUtils";
import type { MailboxInfo } from "owa-client-types";

export type MoveCopyDialogScenario = "move" | "copy" | "attach" | "categorize";
/**
 * Shows a confirmation dialog for cross account with a localized message for move copy depending on the scenario.
 * @returns Promise<boolean> Resolves to true if confirmed or if not a cross account operation, false otherwise.
 */
export default function checkIfCrossAccountAndShowConfirmationDialogIfNeeded(type: MoveCopyDialogScenario, sourceMailboxInfo: MailboxInfo, destinationFolderIdOrMailboxInfo: string | MailboxInfo, projectionTabId?: string): Promise<boolean> {
    const destinationMailboxInfo = typeof destinationFolderIdOrMailboxInfo === "string"
        ? getDestinationMailboxInfo(sourceMailboxInfo, destinationFolderIdOrMailboxInfo)
        : destinationFolderIdOrMailboxInfo;
    const isCrossAccount = isMoveCopyCrossAccount(sourceMailboxInfo, destinationMailboxInfo);
    const isCrossAccountNotAllowed = isMoveCopyCrossAccountNotAllowedPolicy(sourceMailboxInfo, destinationMailboxInfo);
    
    const globalSettingsMailboxInfo = getGlobalSettingsAccountMailboxInfo();
    const isNeverShowDialogSet: boolean = getIsBitSet(32768, globalSettingsMailboxInfo);
    
    // Decide if we need to show the dialog
    const shouldShowDialog = isCrossAccountNotAllowed || (isCrossAccount && !isNeverShowDialogSet);
    
    if (!shouldShowDialog) {
        return Promise.resolve(true);
    }
    
    const destinationEmailAddress = destinationMailboxInfo.mailboxSmtpAddress;
    const { title: titleLocString, message: confirmDialogMessage } = getCrossAccountDialogText(type, isCrossAccountNotAllowed, destinationEmailAddress);
    
    return showDialogAndHandleResponse(titleLocString, confirmDialogMessage, isCrossAccountNotAllowed, type, sourceMailboxInfo, destinationMailboxInfo, globalSettingsMailboxInfo, projectionTabId);
}

function showDialogAndHandleResponse(title: string, message: string, isCrossAccountNotAllowed: boolean, type: MoveCopyDialogScenario, sourceMailboxInfo: MailboxInfo, destinationMailboxInfo: MailboxInfo, globalSettingsMailboxInfo: MailboxInfo, projectionTabId: string | undefined): Promise<boolean> {
    return new Promise<boolean>(resolve => {
        setMoveCopyAttachCrossAccountDialogStateMutator(true /* isOpen */, title, message, isCrossAccountNotAllowed, type, sourceMailboxInfo, destinationMailboxInfo, globalSettingsMailboxInfo, resolve, projectionTabId);
    });
}

function getCrossAccountDialogText(type: MoveCopyDialogScenario, isCrossAccountNotAllowed: boolean, destinationEmailAddress: string): {
    title: string;
    message: string;
} {
    if (isCrossAccountNotAllowed) {
        switch (type) {
            case "move":
                return {
                    title: loc(crossAccountNotAllowedMoveTitleText),
                    message: loc(moveCrossAccountNotAllowedContentText),
                };
            case "copy":
                return {
                    title: loc(crossAccountNotAllowedCopyTitleText),
                    message: loc(copyCrossAccountNotAllowedContentText),
                };
            case "attach":
                return {
                    title: loc(crossAccountNotAllowedAttachTitleText),
                    message: loc(attachCrossAccountNotAllowedContentText),
                };
            case "categorize":
                return {
                    title: loc(crossAccountNotAllowedCategorizeTitleText),
                    message: loc(categorizeCrossAccountNotAllowedContentText),
                };
            default:
                assertNever(type);
        }
    }
    else {
        switch (type) {
            case "move":
                return {
                    title: loc(movingEmailConfirmDialogTitle),
                    message: format(loc(movingEmailConfirmMessage), destinationEmailAddress),
                };
            case "copy":
                return {
                    title: loc(copyingEmailConfirmDialogTitle),
                    message: format(loc(copyingEmailConfirmMessage), destinationEmailAddress),
                };
            case "attach":
                return {
                    title: loc(attachingFileConfirmDialogTitle),
                    message: format(loc(attachingFileConfirmMessage), destinationEmailAddress),
                };
            case "categorize":
                return {
                    title: loc(categorizingEmailConfirmDialogTitle),
                    message: format(loc(categorizingEmailConfirmMessage), destinationEmailAddress),
                };
            default:
                assertNever(type);
        }
    }
}
